import React from "react";
import { useTheme } from "styled-components";

import { IUser } from "@typings/user";

import { Dropdown } from "@components/Navigation/Dropdown";

import { SingleUser } from "@ui/Assets/MyIcons";
import { TestWrapper } from "@ui/Atoms/TestWrapper";

import { DrawerManager } from "@features/DrawerManager/DrawerManager.www";

import { useBreakpoint } from "@hooks/useBreakpoint";
import { useAuth, useAuthManager } from "@hooks/useAuth";
import { useTranslate } from "@hooks/useTranslate";
import { useTelemetry } from "@hooks/useTelemetry";

import { CustomDrawerHeader } from "../CustomDrawerHeader";

import * as Styled from "./AccountMenu.styled";
import { AccountNav } from "./AccountMenu.nav";

const accountDrawerId = "account-drawer";

const getUserName = (user: IUser): string => {
  if (!user.firstName) {
    return user.userName;
  }

  return `${user.firstName} ${user.lastName}`;
};

export const AccountMenu: React.FC = () => {
  const theme = useTheme();
  const translate = useTranslate("batmaid_pwa_navigation");
  const { mobile, largeMobile } = useBreakpoint();
  const { user, isUserLoggedIn } = useAuth();
  const { openLoginModal: login } = useAuthManager();
  const { elementClick } = useTelemetry();
  const { showDrawer, hideDrawer, isDrawerActive } = DrawerManager.useManager();

  const [dropdownActive, setDropdownActive] = React.useState(false);

  const isMobile = mobile || largeMobile;

  const openLoginModal = (label = "") => {
    login();

    elementClick(label, {
      buttonId: "openLoginModal",
    });
  };

  const buttonClickHandler = () => {
    elementClick("", { buttonId: "openAccountMenu" });

    if (isUserLoggedIn) {
      showDrawer(accountDrawerId);
    } else {
      openLoginModal();
    }
  };

  const navLinkClickHandler = (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ): void => {
    elementClick(event.currentTarget.textContent || "");

    if (isDrawerActive(accountDrawerId)) {
      hideDrawer(accountDrawerId);
    }

    if (dropdownActive) {
      setDropdownActive(false);
    }
  };

  React.useEffect(() => {
    if (isMobile) {
      hideDrawer(accountDrawerId);
    }
  }, [isMobile]);

  return (
    <React.Fragment>
      <TestWrapper testId="accountMenuButton">
        <Styled.MobileButton onClick={buttonClickHandler}>
          <SingleUser width={theme.icons.sizes.base_x3} />
        </Styled.MobileButton>
      </TestWrapper>

      {isMobile && isUserLoggedIn && (
        <DrawerManager
          testId="accountMenuDrawer"
          id={accountDrawerId}
          customHeader={
            <CustomDrawerHeader
              crossButton={{
                onClick: () => {
                  hideDrawer(accountDrawerId);
                  elementClick("crossButton", { buttonId: "closeAccountMenu" });
                },
              }}
            />
          }
        >
          <AccountNav onClick={navLinkClickHandler} />
        </DrawerManager>
      )}

      {!isUserLoggedIn && (
        <TestWrapper testId="accountMenuLoginButton">
          <Styled.NonMobileButton
            onClick={openLoginModal.bind(null, translate("login"))}
          >
            {translate("login")}
          </Styled.NonMobileButton>
        </TestWrapper>
      )}

      {!isMobile && isUserLoggedIn && !!user && (
        <Dropdown
          testIds={{
            button: "accountMenuDropdownButton",
            panel: "accountMenuDropdownPanel",
          }}
          eventId="accountDropdown"
          eventLabel="***** *****"
          label={getUserName(user.toObject())}
          labelEllipsis
          position="right"
          distance={14}
          isActive={dropdownActive}
          setIsActive={setDropdownActive}
        >
          <AccountNav onClick={navLinkClickHandler} />
        </Dropdown>
      )}
    </React.Fragment>
  );
};
